import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Check, CheckCheck, Smile, Pin, PinOff, Mic, Play, Pause, Download, Link2
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const formatReadTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
};

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (fileType) => {
  if (fileType?.startsWith('image/')) return '🖼️';
  if (fileType?.includes('pdf')) return '📄';
  if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
  if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊';
  if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return '📽️';
  if (fileType?.includes('zip') || fileType?.includes('rar')) return '📦';
  return '📎';
};

export const MessageBubble = ({
  msg,
  isOwn,
  readStatus,
  userId,
  availableEmojis,
  activeReactionPicker,
  setActiveReactionPicker,
  onAddReaction,
  onRemoveReaction,
  onPinMessage,
  onUnpinMessage,
  playingVoiceId,
  onTogglePlayVoice
}) => {
  const { t } = useLanguage();
  const msgReadStatus = readStatus[msg.message_id] || { read: msg.read, read_at: msg.read_at };
  const isRead = msgReadStatus.read;
  const readAt = msgReadStatus.read_at;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`flex gap-2 max-w-[70%] ${isOwn ? 'flex-row-reverse' : ''}`}>
        {!isOwn && (
          <Avatar className="w-8 h-8 flex-shrink-0">
            <AvatarImage src={msg.sender_picture} />
            <AvatarFallback>{msg.sender_name?.charAt(0)}</AvatarFallback>
          </Avatar>
        )}
        <div>
          <div className="relative group">
            <div className={`rounded-2xl px-4 py-2 ${
              isOwn 
                ? 'bg-pink-500 text-white rounded-br-sm' 
                : 'bg-slate-800 rounded-bl-sm'
            }`}>
              {/* Voice Message */}
              {msg.message_type === 'voice' && msg.voice_url ? (
                <div className="flex items-center gap-3 min-w-[200px]">
                  <button
                    onClick={() => onTogglePlayVoice(msg.message_id, msg.voice_url)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isOwn ? 'bg-white/20 hover:bg-white/30' : 'bg-slate-700 hover:bg-slate-600'}`}
                  >
                    {playingVoiceId === msg.message_id ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5" />
                    )}
                  </button>
                  <div className="flex-1">
                    <div className={`h-1 rounded-full ${isOwn ? 'bg-white/30' : 'bg-slate-600'}`}>
                      <div className={`h-full rounded-full ${isOwn ? 'bg-white' : 'bg-pink-500'}`} style={{ width: playingVoiceId === msg.message_id ? '50%' : '0%' }} />
                    </div>
                    <span className="text-xs opacity-70 mt-1">
                      {formatDuration(msg.voice_duration || 0)}
                    </span>
                  </div>
                  <Mic className="w-4 h-4 opacity-50" />
                </div>
              ) : msg.message_type === 'file' && msg.file_attachment ? (
                /* File Attachment */
                <a 
                  href={`${API}${msg.file_attachment.file_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 p-2 rounded-lg ${isOwn ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-700 hover:bg-slate-600'} transition-colors`}
                >
                  <div className="text-2xl">{msg.file_attachment.file_icon || getFileIcon(msg.file_attachment.file_type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{msg.file_attachment.file_name}</p>
                    <p className="text-xs opacity-60">{msg.file_attachment.file_size_formatted || formatFileSize(msg.file_attachment.file_size)}</p>
                  </div>
                  <Download className="w-4 h-4 opacity-60" />
                </a>
              ) : msg.message_type === 'image' && msg.file_attachment ? (
                /* Image Attachment */
                <div>
                  <a 
                    href={`${API}${msg.file_attachment.file_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img 
                      src={`${API}${msg.file_attachment.file_url}`}
                      alt={msg.file_attachment.file_name}
                      className="max-w-[300px] max-h-[200px] rounded-lg object-cover"
                    />
                  </a>
                  {msg.content && msg.content !== `🖼️ ${msg.file_attachment.file_name}` && (
                    <p className="text-sm mt-2">{msg.content}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              )}
              
              {/* Link Preview */}
              {msg.link_preview && (
                <a 
                  href={msg.link_preview.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`block mt-2 rounded-lg overflow-hidden border ${isOwn ? 'border-white/20 bg-white/10' : 'border-slate-700 bg-slate-900'}`}
                >
                  {msg.link_preview.image && (
                    <img 
                      src={msg.link_preview.image} 
                      alt="" 
                      className="w-full h-32 object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="p-2">
                    <div className="flex items-center gap-1 text-xs opacity-60 mb-1">
                      <Link2 className="w-3 h-3" />
                      {msg.link_preview.site_name || new URL(msg.link_preview.url).hostname}
                    </div>
                    <p className={`text-sm font-medium line-clamp-1 ${isOwn ? 'text-white' : 'text-slate-200'}`}>
                      {msg.link_preview.title}
                    </p>
                    {msg.link_preview.description && (
                      <p className={`text-xs line-clamp-2 mt-1 ${isOwn ? 'text-white/70' : 'text-slate-400'}`}>
                        {msg.link_preview.description}
                      </p>
                    )}
                  </div>
                </a>
              )}
            </div>
            
            {/* Reaction Button */}
            <Popover open={activeReactionPicker === msg.message_id} onOpenChange={(open) => setActiveReactionPicker(open ? msg.message_id : null)}>
              <PopoverTrigger asChild>
                <button 
                  className={`absolute ${isOwn ? '-left-8' : '-right-8'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-slate-700 hover:bg-slate-600`}
                  data-testid={`reaction-btn-${msg.message_id}`}
                >
                  <Smile className="w-4 h-4 text-slate-300" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2 bg-slate-800 border-slate-700" side={isOwn ? "left" : "right"}>
                <div className="flex gap-1">
                  {availableEmojis.map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => onAddReaction(msg.message_id, emoji)}
                      className="text-xl hover:scale-125 transition-transform p-1 rounded hover:bg-slate-700"
                      data-testid={`emoji-${emoji}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Pin Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  className={`absolute ${isOwn ? '-left-16' : '-right-16'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full ${msg.pinned ? 'bg-amber-600 text-white' : 'bg-slate-700 hover:bg-slate-600'}`}
                  onClick={() => msg.pinned ? onUnpinMessage(msg.message_id) : onPinMessage(msg.message_id)}
                  data-testid={`pin-btn-${msg.message_id}`}
                >
                  {msg.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4 text-slate-300" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side={isOwn ? "left" : "right"} className="text-xs">
                {msg.pinned ? t('unpin_message') : t('pin_message')}
              </TooltipContent>
            </Tooltip>
            
            {/* Pinned Badge */}
            {msg.pinned && (
              <div className={`absolute ${isOwn ? '-left-1' : '-right-1'} -top-2`}>
                <Pin className="w-3 h-3 text-amber-500 fill-amber-500" />
              </div>
            )}
            
            {/* Display Reactions */}
            {msg.reactions && msg.reactions.length > 0 && (
              <div className={`flex gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                {msg.reactions.map((reaction, rIdx) => {
                  const isMyReaction = reaction.user_id === userId;
                  return (
                    <Tooltip key={rIdx}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => isMyReaction && onRemoveReaction(msg.message_id)}
                          className={`text-sm px-2 py-0.5 rounded-full transition-all ${
                            isMyReaction 
                              ? 'bg-pink-500/20 border border-pink-500 hover:bg-pink-500/30' 
                              : 'bg-slate-700 border border-slate-600'
                          }`}
                        >
                          {reaction.emoji}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {reaction.user_name}{isMyReaction && ' (click to remove)'}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
            <span className="text-xs text-slate-500">
              {formatTime(msg.created_at)}
            </span>
            {isOwn && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">
                    {isRead ? (
                      <CheckCheck className="w-4 h-4 text-blue-400" />
                    ) : (
                      <Check className="w-4 h-4 text-slate-500" />
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-xs">
                  {isRead ? (
                    <span>{t('seen')} {readAt ? formatReadTime(readAt) : ''}</span>
                  ) : (
                    <span>{t('delivered')}</span>
                  )}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MessageBubble;
